import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { LogoHorizontal } from './Logo';
import AnnouncementBanner from './AnnouncementBanner';
import api from '../api/api';
import {
  LayoutDashboard,
  Package,
  Truck,
  Calculator,
  Wallet,
  Receipt,
  Settings,
  ChevronLeft,
  Plus,
  LogOut,
  Users,
  Shield,
  HelpCircle,
  DollarSign,
  Gift,
  BarChart3,
  Gauge,
  Plug,
  Inbox,
  FileText,
  Map,
  ClipboardList,
  Boxes
} from 'lucide-react';

// Menu items for different roles
const customerMenuItems = [
  { path: '/customer', icon: LayoutDashboard, label: 'Dashboard' },
  { path: '/orders', icon: Package, label: 'Orders' },
  { path: '/rates', icon: Calculator, label: 'Rate Calculator' },
  { path: '/qna', icon: HelpCircle, label: 'Q & A' },
  { path: '/wallet', icon: Wallet, label: 'Wallet' },
  { path: '/invoices', icon: Receipt, label: 'Invoice' },
  { path: '/products', icon: Boxes, label: 'Products' },
  { path: '/integrations/inbox', icon: Inbox, label: 'Order Inbox' },
  { path: '/settings', icon: Settings, label: 'Settings' },
];

const adminMenuItems = [
  { path: '/admin', icon: LayoutDashboard, label: 'Dashboard' },
  { path: '/admin/order-management', icon: ClipboardList, label: 'Order Management' },
  { path: '/admin/pickup-management', icon: Package, label: 'Pickup Management' },
  { path: '/admin/rate-management', icon: DollarSign, label: 'Rate Management' },
  { path: '/admin/wallet', icon: Wallet, label: 'Wallet Management' },
  { path: '/admin/kyc', icon: Shield, label: 'KYC Management' },
  { path: '/admin/users', icon: Users, label: 'User Management' },
  { path: '/admin/qna', icon: HelpCircle, label: 'Q & A Management' },
  { path: '/admin/invoices', icon: FileText, label: 'Invoices' },
  { path: '/admin/products', icon: Boxes, label: 'Products' },
  { path: '/admin/analytics', icon: BarChart3, label: 'Analytics' },
  { path: '/admin/performance', icon: Gauge, label: 'Performance' },
];

const pickupManMenuItems = [
  { path: '/pickupman', icon: LayoutDashboard, label: 'Dashboard' },
  { path: '/pickupman/map', icon: Map, label: 'Map View' },
  { path: '/settings', icon: Settings, label: 'Settings' },
];

const DashboardLayout = ({ children }) => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [recentRewards, setRecentRewards] = useState([]);

  // Fetch last reward for navbar
  useEffect(() => {
    if (user?.role === 'customer') {
      const fetchRewards = async () => {
        try {
          const res = await api.get('/wallet/rewards');
          const rewards = (res.data.data || []).filter(t => t.type === 'reward' && t.status === 'credited');
          setRecentRewards(rewards.slice(0, 3).map(t => ({
            id: t.id,
            amount: parseFloat(t.amount).toFixed(2),
            orderId: t.reference_id
          })));
        } catch (_) { /* ignored */ }
      };
      fetchRewards();
    }
  }, [user]);

  // Live-location heartbeat: pickup men report GPS every 30s while logged in.
  useEffect(() => {
    if (user?.role !== 'pickup_man') return;
    if (!('geolocation' in navigator)) return;

    let cancelled = false;
    const report = () => {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          if (cancelled) return;
          api.post('/pickupman/live-location', {
            lat: pos.coords.latitude,
            lng: pos.coords.longitude,
            accuracy_m: pos.coords.accuracy,
          }).catch(() => { /* ignored — best effort */ });
        },
        () => { /* ignored — permission denied / unavailable */ },
        { enableHighAccuracy: true, maximumAge: 20000, timeout: 15000 }
      );
    };

    report();
    const iv = setInterval(report, 30000);
    return () => { cancelled = true; clearInterval(iv); };
  }, [user]);


  // Get menu items based on user role
  const getMenuItems = () => {
    if (user?.role === 'admin') return adminMenuItems;
    if (user?.role === 'pickup_man') return pickupManMenuItems;
    return customerMenuItems;
  };

  const menuItems = getMenuItems();

  // Active menu = the longest (most specific) path that matches the current URL.
  // Prevents root items like /admin or /pickupman from staying active on sub-routes.
  const activePath = menuItems.reduce((best, it) => {
    const match = location.pathname === it.path || location.pathname.startsWith(it.path + '/');
    return match && (!best || it.path.length > best.length) ? it.path : best;
  }, '');

  // Get dashboard path based on role
  const getDashboardPath = () => {
    if (user?.role === 'admin') return '/admin';
    if (user?.role === 'pickup_man') return '/pickupman';
    return '/customer';
  };

  // Get role badge color
  const getRoleBadge = () => {
    if (user?.role === 'admin') return { label: 'Admin', color: 'bg-red-100 text-red-700' };
    if (user?.role === 'pickup_man') return { label: 'Pickup Man', color: 'bg-purple-100 text-purple-700' };
    return { label: 'Customer', color: 'bg-blue-100 text-blue-700' };
  };

  const roleBadge = getRoleBadge();

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const getUserInitials = () => {
    if (user?.first_name && user?.last_name) {
      return `${user.first_name.charAt(0)}${user.last_name.charAt(0)}`.toUpperCase();
    }
    return 'NU';
  };

  return (
    <div className="min-h-screen bg-white flex flex-col lg:flex-row">
      {/* Desktop Sidebar - Hidden on mobile */}
      <aside className={`hidden lg:block bg-white border-r border-gray-100 fixed h-full transition-all duration-300 z-30 ${sidebarCollapsed ? 'w-16' : 'w-52'}`}>
        {/* Collapse Button */}
        <div className="p-4 flex justify-center">
          <button
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center hover:bg-gray-200 transition-colors"
          >
            <ChevronLeft size={18} className={`text-gray-500 transition-transform ${sidebarCollapsed ? 'rotate-180' : ''}`} />
          </button>
        </div>

        {/* Navigation */}
        <nav className="px-2 space-y-1">
          {menuItems.map((item) => {
            const Icon = item.icon;
            const isActive = item.path === activePath;
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${
                  isActive 
                    ? 'bg-blue-50 text-blue-600' 
                    : 'text-gray-600 hover:bg-gray-50'
                } ${sidebarCollapsed ? 'justify-center' : ''}`}
                title={sidebarCollapsed ? item.label : ''}
              >
                <Icon size={20} />
                {!sidebarCollapsed && <span className="font-medium text-sm">{item.label}</span>}
              </Link>
            );
          })}
        </nav>
      </aside>

      {/* Main Content */}
      <div className={`flex-1 flex flex-col min-h-screen transition-all duration-300 pb-20 lg:pb-0 ${sidebarCollapsed ? 'lg:ml-16' : 'lg:ml-52'}`}>
        {/* Customer top-line announcement */}
        {user?.role === 'customer' && <AnnouncementBanner />}

        {/* Header */}
        <header className="bg-white border-b border-gray-100 sticky top-0 z-30">
          <div className="flex items-center justify-between px-4 lg:px-6 py-3">
            {/* Left - Logo (clickable to dashboard) */}
            <Link to={getDashboardPath()} className="flex items-center">
              <LogoHorizontal variant="dark" size="small" />
            </Link>

            {/* Center - Last 3 Rewards - Show for customers */}
            {user?.role === 'customer' && (
              <Link
                to="/wallet?tab=rewards"
                className="hidden md:flex items-center gap-2 bg-purple-50 text-purple-700 px-4 py-2 rounded-lg hover:bg-purple-100 transition-colors"
                data-testid="navbar-rewards"
              >
                <Gift size={18} className="text-purple-600" />
                <span className="text-sm font-medium text-purple-600 mr-1">Recent Rewards:</span>
                <div className="flex items-center gap-1">
                  {recentRewards.length > 0 ? recentRewards.map((reward, index) => (
                    <span key={reward.id} className="flex items-center">
                      <span className="text-sm font-bold text-purple-700">+₹{reward.amount}</span>
                      {index < recentRewards.length - 1 && (
                        <span className="mx-2 text-purple-300">|</span>
                      )}
                    </span>
                  )) : (
                    <span className="text-sm font-bold text-purple-700">₹0</span>
                  )}
                </div>
              </Link>
            )}

            {/* Right Section */}
            <div className="flex items-center gap-2 lg:gap-3">
              {/* Wallet Balance / Recharge - Show for customers */}
              {user?.role === 'customer' && (
                <Link
                  to="/wallet?tab=recharge"
                  className="flex items-center gap-1 lg:gap-2 bg-green-50 text-green-600 px-2 lg:px-4 py-2 rounded-lg hover:bg-green-100 transition-colors"
                >
                  <Wallet size={18} />
                  <span className="font-semibold text-sm lg:text-base">₹ {user?.wallet_balance?.toFixed(2) || '0.00'}</span>
                  <span className="hidden sm:inline text-green-700 font-medium">Recharge</span>
                </Link>
              )}

              {/* Add Order Button - Show for customers */}
              {user?.role === 'customer' && (
                <Link
                  to="/orders/add"
                  className="w-9 h-9 lg:w-10 lg:h-10 bg-blue-600 rounded-full flex items-center justify-center text-white hover:bg-blue-700 transition-colors flex-shrink-0"
                  title="Add Order"
                >
                  <Plus size={18} />
                </Link>
              )}
              
              {/* User Avatar with Last Login — inline */}
              <div className="relative group flex-shrink-0">
                <div className="flex items-center gap-2">
                  <button className="w-9 h-9 lg:w-10 lg:h-10 rounded-full border-2 border-gray-800 flex items-center justify-center text-gray-800 font-bold text-xs lg:text-sm hover:bg-gray-50">
                    {getUserInitials()}
                  </button>
                  {user?.last_login && (
                    <span className="text-[9px] leading-tight text-gray-400 whitespace-nowrap hidden lg:block">
                      Last login<br/>{(() => {
                        const raw = user.last_login;
                        const d = new Date(typeof raw === 'string' && !raw.endsWith('Z') && !raw.includes('+') ? raw + 'Z' : raw);
                        return d.toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', hour12: true });
                      })()}
                    </span>
                  )}
                </div>
                
                {/* Dropdown - with invisible bridge for better hover */}
                <div className="absolute right-0 pt-2 w-56 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                  <div className="bg-white rounded-lg shadow-xl border border-gray-200 overflow-hidden">
                    <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
                      <div className="flex items-center justify-between mb-1">
                        <p className="font-semibold text-gray-900">{user?.first_name || 'User'} {user?.last_name || ''}</p>
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${roleBadge.color}`}>
                          {roleBadge.label}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 truncate">{user?.email || 'user@example.com'}</p>
                    </div>
                    <button
                      onClick={handleLogout}
                      className="flex items-center gap-2 px-4 py-3 text-red-600 hover:bg-red-50 w-full text-left transition-colors"
                    >
                      <LogOut size={16} />
                      Logout
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 p-4 lg:p-6">
          {children}
        </main>

        {/* Desktop Footer */}
        <footer className="hidden lg:block text-center py-4 text-gray-400 text-sm">
          2026 © ShipReward.in
        </footer>
      </div>

      {/* Mobile Bottom Navigation */}
      <nav className="lg:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 z-30">
        <div className="overflow-x-auto scrollbar-hide">
          <div className="flex items-center px-2 py-2 min-w-max">
            {menuItems.map((item) => {
              const Icon = item.icon;
              const isActive = item.path === activePath;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex flex-col items-center justify-center px-4 py-2 min-w-[70px] rounded-lg transition-colors ${
                    isActive 
                      ? 'bg-blue-50 text-blue-600' 
                      : 'text-gray-500 hover:bg-gray-50'
                  }`}
                >
                  <Icon size={20} />
                  <span className="text-[10px] mt-1 font-medium whitespace-nowrap">{item.label}</span>
                </Link>
              );
            })}
          </div>
        </div>
      </nav>
    </div>
  );
};

export default DashboardLayout;
